import React from "react";

import { Card, CardContent, CardHeader, CardTitle } from "../../components/misc/Card";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import { AccountHealthFilter, AccountHealthOverview, TIER_DOT } from "./types";

interface Tile {
    key: Exclude<AccountHealthFilter, "all">;
    title: string;
    hint: string;
    count: number;
    dot?: string;
}

interface AccountHealthMetricCardsProps {
    counts: AccountHealthOverview["counts"];
    active: AccountHealthFilter;
    onSelect: (filter: AccountHealthFilter) => void;
    className?: string;
}

// The digest subject's three counts plus its plan-full section. A tile is also
// a shortcut to that bucket, so the number and the list stay one click apart.
const AccountHealthMetricCards: React.FC<AccountHealthMetricCardsProps> = ({
    counts,
    active,
    onSelect,
    className,
}) => {
    const isMobile = useIsMobile(900);
    const tiles: Tile[] = [
        { key: "at-risk", title: "At Risk", hint: "Act now", count: counts.atRisk, dot: TIER_DOT["at-risk"] },
        { key: "watch", title: "Watch", hint: "Watch closely", count: counts.watch, dot: TIER_DOT.watch },
        { key: "healthy", title: "Healthy", hint: "No warning fired", count: counts.healthy, dot: TIER_DOT.healthy },
        { key: "plan-full", title: "Plan full", hint: "Upgrade conversations", count: counts.planFull },
    ];

    return (
        <div className={cn("grid gap-4", isMobile ? "grid-cols-2" : "grid-cols-4", className)}>
            {tiles.map((t) => (
                <div
                    key={t.key}
                    data-testid={`tile-${t.key}`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={active === t.key}
                    onClick={() => onSelect(t.key)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelect(t.key);
                        }
                    }}
                    // focus-visible, not focus: a tile clicked back to "All" keeps
                    // focus and must not keep looking selected.
                    className="cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    <Card className={cn("mt-0 flex h-full flex-col", active === t.key && "ring-2 ring-primary")}>
                        <CardHeader className="p-4 pb-1">
                            <CardTitle className="flex items-center gap-2 text-left text-sm font-semibold">
                                {t.dot && (
                                    <span aria-hidden="true" className={cn("inline-block h-2.5 w-2.5 rounded-full", t.dot)} />
                                )}
                                {t.title}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                            <div className="text-2xl font-bold text-primary">{t.count}</div>
                            <div className="text-xs text-gray-500">{t.hint}</div>
                        </CardContent>
                    </Card>
                </div>
            ))}
        </div>
    );
};

export default AccountHealthMetricCards;
